import { useEffect, useState, type ReactNode } from "react";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { ordersLabels } from "@/i18n";
import { useIsMobile } from "@/hooks/use-mobile";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { OrdersView } from "@/components/orders/OrdersView";
import { MyBiddingsView } from "@/components/orders/MyBiddingsView";
import { useSearchParams } from "react-router-dom";
import { MobilePillTabs } from "@/components/ui/mobile-pill-tabs";
import { useShowNavigateBack } from "@/hooks/useShowNavigateBack";
import { ListingsOrdersTabs } from "@/components/dashboard/ListingsOrdersTabs";
import {
  markBuyerAttentionSeen,
  useMyObjectsAttention,
} from "@/hooks/useMyObjectsAttention";

type OrderFilter = "all" | "ej-levererade" | "levererade" | "mina-budgivningar";

const Orders = () => {
  const [currentFilter, setCurrentFilter] = useState<OrderFilter>("all");
  const { theme } = useTheme();
  const { language } = useLanguage();
  const o = ordersLabels(language);
  const isDark = theme === "dark";
  const isMobile = useIsMobile();
  const showPageBack = useShowNavigateBack();
  const [searchParams] = useSearchParams();
  const { buyerNeedsAction, pendingBuyerContractIds } = useMyObjectsAttention();

  useEffect(() => {
    if (pendingBuyerContractIds.length > 0) {
      markBuyerAttentionSeen(pendingBuyerContractIds);
    }
  }, [pendingBuyerContractIds]);

  const orderTabItems: { value: OrderFilter; label: ReactNode }[] = [
    {
      value: "all",
      label: (
        <span className="inline-flex items-center gap-1.5">
          {o.filters.allShort}
          {buyerNeedsAction ? (
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden />
          ) : null}
        </span>
      ),
    },
    { value: "ej-levererade", label: o.filters.notDelivered },
    { value: "levererade", label: o.filters.delivered },
    { value: "mina-budgivningar", label: o.filters.myBidsShort },
  ];

  // Allow deep-linking to specific tabs, e.g. /orders?tab=my-biddings
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (!tab) return;

    if (tab === "my-biddings" || tab === "my-bids" || tab === "mina-budgivningar") {
      setCurrentFilter("mina-budgivningar");
      return;
    }

    if (tab === "not-delivered" || tab === "ej-levererade") {
      setCurrentFilter("ej-levererade");
      return;
    }

    if (tab === "delivered" || tab === "levererade") {
      setCurrentFilter("levererade");
      return;
    }

    if (tab === "all") {
      setCurrentFilter("all");
    }
  }, [searchParams]);

  const renderContent = () => {
    if (currentFilter === "mina-budgivningar") {
      return <MyBiddingsView />;
    }
    return <OrdersView filter={currentFilter} />;
  };

  return (
    <div className="container mx-auto px-4 py-2 relative z-10">
      {showPageBack ? (
        <div className="mb-3">
          <BtnNavigateBack />
        </div>
      ) : null}

      <ListingsOrdersTabs active="orders" />

      <div className="flex flex-col md:flex-row gap-4">
        {/* Left Navigation Menu - Only visible on desktop */}
        {!isMobile && (
          <div className="md:w-64 flex-shrink-0">
            <h2
              className={`text-2xl font-bold ${
                isDark ? "text-accent" : "text-accent"
              } mb-4`}
            >
              {o.title}
            </h2>
            <nav className="flex flex-col space-y-2">
              <button
                onClick={() => setCurrentFilter("all")}
                className={`relative text-left py-2 px-4 rounded-md transition-colors ${
                  currentFilter === "all"
                    ? isDark
                      ? "text-white bg-secondary border-l-2 border-accent"
                      : "text-black bg-secondary border-l-2 border-accent"
                    : "hover:bg-secondary/50"
                }`}
              >
                {o.filters.allShort}
                {buyerNeedsAction ? (
                  <span
                    className="absolute top-2.5 right-3 h-2 w-2 rounded-full bg-red-500"
                    aria-hidden
                  />
                ) : null}
              </button>
              <button
                onClick={() => setCurrentFilter("ej-levererade")}
                className={`text-left py-2 px-4 rounded-md transition-colors ${
                  currentFilter === "ej-levererade"
                    ? isDark
                      ? "text-white bg-secondary border-l-2 border-accent"
                      : "text-black bg-secondary border-l-2 border-accent"
                    : "hover:bg-secondary/50"
                }`}
              >
                {o.filters.notDelivered}
              </button>
              <button
                onClick={() => setCurrentFilter("levererade")}
                className={`text-left py-2 px-4 rounded-md transition-colors ${
                  currentFilter === "levererade"
                    ? isDark
                      ? "text-white bg-secondary border-l-2 border-accent"
                      : "text-black bg-secondary border-l-2 border-accent"
                    : "hover:bg-secondary/50"
                }`}
              >
                {o.filters.delivered}
              </button>

              <div
                className={`my-3 border-t-2 ${
                  isDark ? "border-white/20" : "border-gray-300"
                }`}
              ></div>

              <button
                onClick={() => setCurrentFilter("mina-budgivningar")}
                className={`text-left py-2 px-4 rounded-md transition-all ${
                  currentFilter === "mina-budgivningar"
                    ? isDark
                      ? "text-white bg-accent/20 backdrop-blur border-l-2 border-accent shadow-md"
                      : "text-black bg-accent/10 backdrop-blur border-l-2 border-accent shadow-md"
                    : "hover:bg-accent/10 hover:shadow-sm"
                }`}
              >
                <span className="font-semibold">{o.filters.myBids}</span>
              </button>
            </nav>
          </div>
        )}

        <div className="flex-1 animate-fade-in">
          {isMobile && (
            <MobilePillTabs
              items={orderTabItems}
              value={currentFilter}
              onChange={setCurrentFilter}
            />
          )}
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Orders;
